import * as THREE from 'three';
import { edgeLines, setEdgeStyle } from './edges';
import type { EdgeLines } from './edges';
import { axisColors, cssColor } from './palette';

export const VIEW_CUBE_MARGIN_PX = 6;
const VIEW_CUBE_MAX_PX = 150;
const VIEW_CUBE_MIN_PX = 56;
const VIEW_CUBE_SHARE = 0.24;
const REST_OPACITY = 0.45;
const LABEL_OPACITY = 0.82;
const EASE = 0.2;

export interface CubeLayout {
  size: number;
  margin: number;
}

/** The cube's square in the top-right corner of a viewport this many CSS px across. */
export function cubeLayout(width: number, height: number): CubeLayout {
  const size = Math.round(
    THREE.MathUtils.clamp(Math.min(width, height) * VIEW_CUBE_SHARE, VIEW_CUBE_MIN_PX, VIEW_CUBE_MAX_PX),
  );
  return { size, margin: VIEW_CUBE_MARGIN_PX };
}

export type CubeFace = 'right' | 'left' | 'back' | 'front' | 'top' | 'bottom';

export interface ViewCubeOptions {
  labels?: Partial<Record<CubeFace, string>>;
  /** Called as the pointer comes onto the cube and leaves it. */
  onHover?: (over: boolean) => void;
}

// Each face is a 3×3 of tiles: the middle one picks the face, the border
// strips pick an edge view and the corner squares a corner view, as on the
// Fusion and Onshape cubes.
const BORDER = 0.32;
const FRUSTUM = 2.25;
// three's Lambert divides by π, so these read as fractions of π.
const AMBIENT = 2.3;
const KEY = 1.1;
const AXIS_REACH = 2.55;
const LABEL_GAP = 0.3;
const HIDDEN_AXIS_OPACITY = 0.3;
const LABEL_LIFT = 0.002;
const CLICK_SLOP_PX = 4;
const LABEL_TEXTURE_PX = 256;

interface CubeStyle {
  face: number;
  border: number;
  hover: number;
  text: string;
  outline: number;
}

const LIGHT: CubeStyle = { face: 0xe4e1ed, border: 0xdad7e4, hover: 0x9e9cdc, text: '#000000', outline: 0xc8c4d6 };
const DARK: CubeStyle = { face: 0x181927, border: 0x14151f, hover: 0x7370b0, text: '#ffffff', outline: 0x2e2e3f };

// Label "up" is chosen so each face reads the right way round when the camera
// snaps to it: side faces with Z up, top and bottom with +Y up, as the snap
// keeps +Y at the top of the screen for those two.
const FACES = [
  { face: 'right', label: 'Right', normal: [1, 0, 0], up: [0, 0, 1] },
  { face: 'left', label: 'Left', normal: [-1, 0, 0], up: [0, 0, 1] },
  { face: 'back', label: 'Back', normal: [0, 1, 0], up: [0, 0, 1] },
  { face: 'front', label: 'Front', normal: [0, -1, 0], up: [0, 0, 1] },
  { face: 'top', label: 'Top', normal: [0, 0, 1], up: [0, 1, 0] },
  { face: 'bottom', label: 'Bottom', normal: [0, 0, -1], up: [0, 1, 0] },
] as const;

const AXES = [
  { letter: 'X', direction: new THREE.Vector3(1, 0, 0), color: 'x' },
  { letter: 'Y', direction: new THREE.Vector3(0, 1, 0), color: 'y' },
  { letter: 'Z', direction: new THREE.Vector3(0, 0, 1), color: 'z' },
] as const;

const ORIGIN_CORNER = new THREE.Vector3(-1, -1, -1);

function textTexture(text: string, color: string, size: number): THREE.Texture | null {
  const canvas = document.createElement('canvas');
  canvas.width = LABEL_TEXTURE_PX;
  canvas.height = LABEL_TEXTURE_PX;
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.fillStyle = color;
  context.font = `600 ${size}px Inter, system-ui, sans-serif`;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(text, LABEL_TEXTURE_PX / 2, LABEL_TEXTURE_PX / 2);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
}

function tileOffsets(): { offset: number; span: number; step: number }[] {
  const middle = 2 - 2 * BORDER;
  return [
    { offset: -1 + BORDER / 2, span: BORDER, step: -1 },
    { offset: 0, span: middle, step: 0 },
    { offset: 1 - BORDER / 2, span: BORDER, step: 1 },
  ];
}

function outlineSegments(): THREE.BufferGeometry {
  const points: THREE.Vector3[] = [];
  for (const axis of [0, 1, 2]) {
    for (const a of [-1, 1]) {
      for (const b of [-1, 1]) {
        const from = [a, b];
        from.splice(axis, 0, -1);
        const to = [a, b];
        to.splice(axis, 0, 1);
        points.push(new THREE.Vector3(...from), new THREE.Vector3(...to));
      }
    }
  }
  return new THREE.BufferGeometry().setFromPoints(points);
}

export class ViewCube {
  private readonly scene = new THREE.Scene();
  private readonly root = new THREE.Group();
  private readonly camera = new THREE.OrthographicCamera(-FRUSTUM, FRUSTUM, FRUSTUM, -FRUSTUM, 0.1, 10);
  private readonly raycaster = new THREE.Raycaster();
  private readonly regions = new Map<
    string,
    { direction: THREE.Vector3; material: THREE.MeshLambertMaterial; resting: number }
  >();
  private readonly tiles: THREE.Mesh[] = [];
  private readonly style: CubeStyle;
  private readonly viewport = new THREE.Vector4();
  private readonly size = new THREE.Vector2();
  private readonly fades: { material: THREE.Material; opacity: number }[] = [];
  private hovered: string | null = null;
  private over = false;
  private emphasis = REST_OPACITY;
  private pressedAt: { x: number; y: number } | null = null;

  constructor(
    private readonly element: HTMLElement,
    isDark: boolean,
    private readonly onPick: (direction: THREE.Vector3) => void,
    private readonly options: ViewCubeOptions = {},
  ) {
    this.style = isDark ? DARK : LIGHT;
    // In front of the cube: an orthographic ray starts midway between the
    // planes, so a camera centred on the cube would cast from inside it.
    this.camera.position.z = 5;
    this.camera.updateMatrixWorld();
    this.buildFaces();
    this.buildOutline();
    this.buildAxes(isDark);
    this.scene.add(new THREE.AmbientLight(0xffffff, AMBIENT));
    const light = new THREE.DirectionalLight(0xffffff, KEY);
    light.position.set(-0.4, 0.8, 1);
    this.scene.add(light, this.root);
    this.collectFades();
    element.addEventListener('pointermove', this.onMove);
    element.addEventListener('pointerdown', this.onDown);
    element.addEventListener('pointerup', this.onUp);
    element.addEventListener('pointerleave', this.onLeave);
  }

  contains(clientX: number, clientY: number): boolean {
    return this.local(clientX, clientY) !== null;
  }

  render(renderer: THREE.WebGLRenderer, camera: THREE.Camera): void {
    this.root.quaternion.copy(camera.quaternion).invert();
    this.root.updateMatrixWorld();
    this.ease();
    renderer.getSize(this.size);
    renderer.getViewport(this.viewport);
    renderer.clearDepth();
    const { size, margin } = cubeLayout(this.size.x, this.size.y);
    renderer.setViewport(this.size.x - margin - size, this.size.y - margin - size, size, size);
    renderer.render(this.scene, this.camera);
    renderer.setViewport(this.viewport);
  }

  dispose(): void {
    this.element.removeEventListener('pointermove', this.onMove);
    this.element.removeEventListener('pointerdown', this.onDown);
    this.element.removeEventListener('pointerup', this.onUp);
    this.element.removeEventListener('pointerleave', this.onLeave);
    this.root.traverse((child) => {
      const drawable = child as THREE.Mesh<THREE.BufferGeometry, THREE.Material & { map?: THREE.Texture | null }>;
      if (!drawable.material) return;
      // Every Sprite shares one module-level quad.
      if (!(child as THREE.Sprite).isSprite) drawable.geometry.dispose();
      drawable.material.map?.dispose();
      drawable.material.dispose();
    });
  }

  private buildFaces(): void {
    for (const face of FACES) {
      const normal = new THREE.Vector3(...face.normal);
      const up = new THREE.Vector3(...face.up);
      const right = new THREE.Vector3().crossVectors(up, normal);
      const basis = new THREE.Matrix4().makeBasis(right, up, normal);
      for (const across of tileOffsets()) {
        for (const down of tileOffsets()) {
          const direction = normal
            .clone()
            .addScaledVector(right, across.step)
            .addScaledVector(up, down.step);
          const isFace = across.step === 0 && down.step === 0;
          const tile = new THREE.Mesh(
            new THREE.PlaneGeometry(across.span, down.span),
            this.regionMaterial(direction, isFace),
          );
          tile.position.copy(normal).addScaledVector(right, across.offset).addScaledVector(up, down.offset);
          tile.quaternion.setFromRotationMatrix(basis);
          tile.userData.region = direction.toArray().join();
          this.tiles.push(tile);
          this.root.add(tile);
        }
      }
      this.addLabel(this.options.labels?.[face.face] ?? face.label, normal, basis);
    }
  }

  // Text on its own plane just off the face, so tinting a hovered face never
  // tints the lettering with it.
  private addLabel(text: string, normal: THREE.Vector3, basis: THREE.Matrix4): void {
    const map = textTexture(text, this.style.text, 52);
    if (!map) return;
    const span = 2 - 2 * BORDER;
    const label = new THREE.Mesh(
      new THREE.PlaneGeometry(span, span),
      new THREE.MeshBasicMaterial({
        map,
        transparent: true,
        opacity: LABEL_OPACITY,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    label.position.copy(normal).multiplyScalar(1 + LABEL_LIFT);
    label.quaternion.setFromRotationMatrix(basis);
    label.raycast = () => {};
    this.root.add(label);
  }

  private regionMaterial(direction: THREE.Vector3, isFace: boolean): THREE.MeshLambertMaterial {
    const key = direction.toArray().join();
    const existing = this.regions.get(key);
    if (existing) return existing.material;
    const resting = isFace ? this.style.face : this.style.border;
    const material = new THREE.MeshLambertMaterial({ color: resting, toneMapped: false, transparent: true });
    this.regions.set(key, { direction: direction.clone().normalize(), material, resting });
    return material;
  }

  private buildOutline(): void {
    const outline = edgeLines(outlineSegments(), 1);
    setEdgeStyle(outline, this.style.outline, 0.9);
    this.root.add(outline);
  }

  private buildAxes(isDark: boolean): void {
    const colors = axisColors(isDark);
    for (const axis of AXES) {
      const color = colors[axis.color];
      const tip = ORIGIN_CORNER.clone().addScaledVector(axis.direction, AXIS_REACH);
      const points = [ORIGIN_CORNER, tip];
      const line: EdgeLines = edgeLines(new THREE.BufferGeometry().setFromPoints(points), 2.25);
      setEdgeStyle(line, color, 1);
      line.renderOrder = 1;
      // The run of an axis the cube hides still shows, faintly, through it.
      const hidden: EdgeLines = edgeLines(new THREE.BufferGeometry().setFromPoints(points), 1.5);
      setEdgeStyle(hidden, color, HIDDEN_AXIS_OPACITY);
      hidden.material.depthTest = false;
      hidden.renderOrder = 2;
      this.root.add(line, hidden);
      const map = textTexture(axis.letter, cssColor(color), 150);
      for (const seen of [true, false]) {
        const label = new THREE.Sprite(
          new THREE.SpriteMaterial({
            map,
            color: map ? 0xffffff : color,
            toneMapped: false,
            depthTest: seen,
            opacity: seen ? 1 : HIDDEN_AXIS_OPACITY,
          }),
        );
        label.renderOrder = 3;
        label.scale.setScalar(0.62);
        label.position.copy(tip).addScaledVector(axis.direction, LABEL_GAP);
        this.root.add(label);
      }
    }
  }

  private local(clientX: number, clientY: number): THREE.Vector2 | null {
    const rect = this.element.getBoundingClientRect();
    const { size, margin } = cubeLayout(rect.width, rect.height);
    const x = clientX - rect.left - (rect.width - margin - size);
    const y = clientY - rect.top - margin;
    if (x < 0 || y < 0 || x > size || y > size) return null;
    return new THREE.Vector2((x / size) * 2 - 1, 1 - (y / size) * 2);
  }

  private collectFades(): void {
    const materials = new Set<THREE.Material>();
    this.root.traverse((child) => {
      const material = (child as THREE.Mesh).material as THREE.Material | undefined;
      if (material) materials.add(material);
    });
    for (const material of materials) {
      material.transparent = true;
      this.fades.push({ material, opacity: material.opacity });
    }
    this.applyEmphasis();
  }

  private ease(): void {
    const target = this.over ? 1 : REST_OPACITY;
    if (Math.abs(target - this.emphasis) < 0.005) {
      if (this.emphasis === target) return;
      this.emphasis = target;
    } else {
      this.emphasis += (target - this.emphasis) * EASE;
    }
    this.applyEmphasis();
  }

  private applyEmphasis(): void {
    for (const { material, opacity } of this.fades) material.opacity = opacity * this.emphasis;
  }

  private setOver(over: boolean): void {
    if (over === this.over) return;
    this.over = over;
    this.options.onHover?.(over);
  }

  private regionAt(clientX: number, clientY: number): string | null {
    const pointer = this.local(clientX, clientY);
    if (!pointer) return null;
    this.root.updateMatrixWorld();
    this.raycaster.setFromCamera(pointer, this.camera);
    const [hit] = this.raycaster.intersectObjects(this.tiles, false);
    return hit ? (hit.object.userData.region as string) : null;
  }

  private highlight(region: string | null): void {
    if (region === this.hovered) return;
    this.tint(this.hovered, false);
    this.tint(region, true);
    this.hovered = region;
    this.element.style.cursor = region ? 'pointer' : '';
  }

  private tint(region: string | null, hovered: boolean): void {
    const entry = region ? this.regions.get(region) : undefined;
    entry?.material.color.setHex(hovered ? this.style.hover : entry.resting);
  }

  private readonly onMove = (event: PointerEvent) => {
    if (event.pointerType === 'touch') return;
    this.setOver(this.contains(event.clientX, event.clientY));
    this.highlight(this.regionAt(event.clientX, event.clientY));
  };

  private readonly onDown = (event: PointerEvent) => {
    this.pressedAt = this.contains(event.clientX, event.clientY)
      ? { x: event.clientX, y: event.clientY }
      : null;
  };

  private readonly onUp = (event: PointerEvent) => {
    const pressedAt = this.pressedAt;
    this.pressedAt = null;
    if (!pressedAt) return;
    if (Math.hypot(event.clientX - pressedAt.x, event.clientY - pressedAt.y) > CLICK_SLOP_PX) return;
    const region = this.regionAt(event.clientX, event.clientY);
    const entry = region ? this.regions.get(region) : undefined;
    if (entry) this.onPick(entry.direction.clone());
  };

  private readonly onLeave = () => {
    this.setOver(false);
    this.highlight(null);
  };
}
